// src/pages/HomePage.jsx
import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Sparkles,
  Heart,
  Users,
  Brain,
  Zap,
  TrendingUp,
  Award,
  ChevronRight,
  Star,
  Coffee,
  Shield,
  Target,
  Lightbulb,
  Globe,
  Smartphone,
  BookOpen,
  Play,
  CheckCircle,
  ArrowRight,
  Quote,
  Mic,
  Gift,
  Clock,
  BarChart2,
  BarChart3,
  Layers,
  FolderOpen
} from 'lucide-react';
import Button from '../components/shared/Button';
import Card from '../components/shared/Card';
import { useAuth } from '../context/AuthContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentBenefitSlide, setCurrentBenefitSlide] = React.useState(0);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);

  // Rotate benefit slides
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBenefitSlide((prev) => (prev + 1) % 3);
    }, 6000); // Change every 6 seconds
    return () => clearInterval(interval);
  }, []);

  const benefitSlides = [
    {
      title: "Stories That Stick",
      subtitle: "Because the stories you tell shape who you become",
      description: "Every story you share reveals identity, builds connection, or squanders both. The difference between rambling and riveting isn't talent; it's technique. Learn the craft of storytelling that transforms ordinary moments into unforgettable narratives."
    },
    {
      title: "Where Will These Skills Take You?",
      subtitle: "From job interviews to dinner parties, first dates to family gatherings",
      description: "Whether you're sharing your origin story in an interview, telling tales over dinner, or helping your kids make sense of family history, how you tell it matters. Same stories, vastly different impact."
    },
    {
      title: "What Makes a Master Storyteller?",
      subtitle: "It's not about being the loudest voice or the funniest person",
      description: "It's about specificity, emotional arcs, and vulnerability calibration. It's the ability to transport listeners into your world, make them feel what you felt, and leave them changed by the encounter."
    }
  ];

  const skillsYoullMaster = [
    {
      title: "Story Inventory",
      subtitle: "Map your narrative landscape",
      description: "Discover which stories you tell, why they matter, and what patterns they reveal about your identity",
      image: "/images/mod-01-inventory.png",
      color: "from-purple-500 to-indigo-500"
    },
    {
      title: "Story Impact",
      subtitle: "The research behind memorable narratives",
      description: "Learn why some stories are easier to follow, believe and remember: research on speaker-listener coupling (Hasson et al., 2012), narrative transportation (Green & Brock, 2000) and story-based memory (Bower & Clark, 1969)",
      image: "/images/mod-02-impact.png",
      color: "from-amber-500 to-orange-500"
    },
    {
      title: "Oscillating Narratives",
      subtitle: "Tell struggle and strength together",
      description: "Turn struggle stories into fuller, more honest ones. In one study of 47 adults in therapy, people began telling their stories with more sense of agency before their mental health improved (Adler, 2012).",
      image: "/images/mod-03-oscillating.png",
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Story Structure",
      subtitle: "A 5-part anatomy for your stories",
      description: "Learn a five-part framework and adapt it for any context: professional, social, intimate or public",
      image: "/images/mod-04-structure.png",
      color: "from-red-500 to-orange-500"
    },
    {
      title: "Emotional Arc",
      subtitle: "Craft journeys that transport listeners",
      description: "Map emotional peaks and valleys, add sensory details that make listeners feel like they were there",
      image: "/images/mod-05-arc.png",
      color: "from-pink-500 to-rose-500"
    },
    {
      title: "Vulnerability Calibration",
      subtitle: "Our 10% rule of thumb for appropriate sharing",
      description: "Learn when to share, how much to reveal, and how to avoid both oversharing and shutting down",
      image: "/images/mod-06-vulnerability.png",
      color: "from-green-500 to-emerald-500"
    },
    {
      title: "Story Delivery",
      subtitle: "Presence, pacing, and confidence",
      description: "Reduce filler words, master strategic pauses, and develop the delivery that makes stories land",
      image: "/images/mod-07-delivery.png",
      color: "from-cyan-500 to-teal-500"
    },
    {
      title: "Story Listening",
      subtitle: "The art of empathic receiving",
      description: "Create space for others' stories without redirecting, minimising, or fixing. Just genuine presence",
      image: "/images/mod-08-story-listening.png",
      color: "from-violet-500 to-purple-500"
    }
  ];

  const realStats = [
    { number: "8", label: "Learning Modules", icon: BookOpen },
    { number: "9", label: "Practical Tools", icon: Target },
    { number: "Personalised", label: "Storytelling Profile", icon: Brain },
    { number: "Your Pace", label: "Self-Guided Learning", icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        {/* Animated Background Elements */}
        <motion.div
          style={{ y: y1 }}
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: y2 }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"
        />

        {/* Floating Art Images */}
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 0.1, x: 0 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          style={{ y: y1 }}
          className="absolute top-40 left-0 w-64 h-64 opacity-10 pointer-events-none"
        >
          <img src="/images/mod-01-inventory.png" alt="" className="w-full h-full object-cover rounded-3xl rotate-12" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 0.1, x: 0 }}
          transition={{ duration: 1.5, delay: 0.7 }}
          style={{ y: y2 }}
          className="absolute bottom-40 right-0 w-72 h-72 opacity-10 pointer-events-none"
        >
          <img src="/images/mod-03-oscillating.png" alt="" className="w-full h-full object-cover rounded-3xl -rotate-12" />
        </motion.div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <img
                src="/art-of-story.svg"
                alt="The Art of Story"
                className="w-32 h-32 md:w-40 md:h-40 mx-auto"
              />
            </motion.div>

            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-8"
            >
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">
                Where storytelling becomes an art form worth mastering
              </span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              The Stories You Tell Yourself
              <span className="block gradient-text">
                Shape Who You Become
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Ever notice how you explain your life to yourself? Those narratives aren't just reflections; they're part of who you become. Learn to recognise the stories that serve you, transform the ones that don't, and share them in ways that genuinely connect.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                size="lg"
                onClick={() => navigate(user ? '/assessment' : '/signup')}
                className="group flex items-center justify-center"
              >
                Start Assessment
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => document.getElementById('skills-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore the Skills
              </Button>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap justify-center gap-6 text-sm text-slate-400"
            >
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                8-10 minute assessment
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Research and craft (but never boring)
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Here's How It Works Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative p-8 md:p-12 bg-slate-800/50 backdrop-blur-xl rounded-3xl border border-slate-700">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl" />
              <div className="relative text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-6">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-300 text-sm font-medium">How it works</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  What You're Getting Into
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Think of this as your storytelling lab. You'll start with an assessment that reveals how you naturally tell stories (and where you might be sabotaging yourself). Then you'll dig into 8 modules that teach you techniques like oscillating narratives (basically, how to tell a struggle story with your response to it in there too) and vulnerability calibration, which is fancy talk for "how much to share without being that person at the party." Plus you get 9 practical tools to actually build and refine your stories for real situations.
                </p>
                <div className="grid md:grid-cols-3 gap-6 text-left">
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                    <Brain className="w-8 h-8 text-purple-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">Self-Guided Learning</h3>
                    <p className="text-sm text-slate-400">Genuine storytelling skills you develop at your own pace, not performative tricks</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                    <Target className="w-8 h-8 text-pink-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">Practical Tools</h3>
                    <p className="text-sm text-slate-400">9 interactive tools to craft, practise, and refine your stories for real situations</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                    <Heart className="w-8 h-8 text-indigo-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">Authenticity-Focussed</h3>
                    <p className="text-sm text-slate-400">Genuine connection through stories, not manipulation or performance</p>
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-slate-700">
                  <p className="text-sm text-slate-400 italic">
                    Part of the Dive Deep Dialogues mission: deeper dialogues build resilience
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills You'll Master Section */}
      <section id="skills-section" className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Eight Skills That Change How You
              <span className="gradient-text"> Tell Your Story</span>
            </h2>
            <p className="text-xl text-slate-300">
              Storytelling isn't some mystical talent you're born with. It's a craft, like making great coffee or playing guitar. And the stories you tell about yourself don't just describe your life; they're part of it. Dan McAdams' work on narrative identity treats your life story as part of who you are, so crafting better stories is identity work, not just performance.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {skillsYoullMaster.map((skill, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-0 h-full overflow-hidden">
                  {/* Art Image Header */}
                  <div className="relative h-40 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${skill.color} opacity-20`} />
                    <img
                      src={skill.image}
                      alt={skill.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{skill.title}</h3>
                    <p className="text-purple-300 text-sm mb-3 italic">{skill.subtitle}</p>
                    <p className="text-slate-300 text-sm leading-relaxed">{skill.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA to Modules */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Button
              size="lg"
              onClick={() => navigate('/modules')}
              className="group inline-flex items-center whitespace-nowrap px-6 sm:px-8"
            >
              <BookOpen className="w-5 h-5 mr-2 flex-shrink-0" />
              Start Your Journey
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="mt-4 text-slate-400 text-sm">
              Unlock modules as you progress • Build skills at your own pace
            </p>
          </motion.div>

          {/* Research Insight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <Card className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <Brain className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <div className="text-center">
                <p className="text-2xl text-white font-medium mb-4">
                  Narrative identity research, pioneered by Dan McAdams at Northwestern University, treats identity as an evolving life story. In a study of 47 adults in therapy, changes in how people told their story came before improvements in their mental health (Adler, 2012).
                </p>
                <footer className="text-slate-400">Based on narrative identity research (McAdams, Northwestern University; Adler, 2012)</footer>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Toolkit Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 rounded-full mb-6">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300 text-sm font-bold uppercase tracking-wider">9 Practical Tools</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Theory's Great. But You Need
                <span className="gradient-text"> Tools That Actually Work</span>
              </h2>

              <p className="text-xl text-slate-300 leading-relaxed">
                This is different because you're not just learning <em>about</em> storytelling. You're building your stories in real time with tools built on what the modules teach. Think of it as having a storytelling workshop in your pocket, one you'll actually use.
              </p>
            </div>

            {/* Tools Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {/* Tool 1 */}
              <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-purple-500/20">
                <TrendingUp className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Oscillating Narrative Builder</h3>
                <p className="text-slate-300 text-sm">
                  Turn "everything went wrong" into "it was hard AND I grew." Psychologist Marshall Duke calls this oscillating pattern "the most healthful narrative" for family stories.
                </p>
              </Card>

              {/* Tool 2 */}
              <Card className="p-6 bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/20">
                <BarChart3 className="w-10 h-10 text-pink-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Emotional Arc Mapper</h3>
                <p className="text-slate-300 text-sm">
                  Map your story's emotional peaks and valleys. See where listeners might zone out, and where they'll lean in.
                </p>
              </Card>

              {/* Tool 3 */}
              <Card className="p-6 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
                <Shield className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Vulnerability Calibrator</h3>
                <p className="text-slate-300 text-sm">
                  Our 10% rule of thumb: how much to share without being "that person at the party" who overshares.
                </p>
              </Card>

              {/* Tool 4 */}
              <Card className="p-6 bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border-cyan-500/20">
                <Layers className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Story Structure Builder</h3>
                <p className="text-slate-300 text-sm">
                  A 5-part framework you can adapt for anything from dinner party anecdotes to job interviews.
                </p>
              </Card>

              {/* Tool 5 */}
              <Card className="p-6 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
                <FolderOpen className="w-10 h-10 text-indigo-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Story Inventory Builder</h3>
                <p className="text-slate-300 text-sm">
                  Discover patterns across the stories you tell. Some are serving you. Others? Maybe not so much.
                </p>
              </Card>

              {/* Tool 6 */}
              <Card className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
                <Brain className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Story Impact Analyser</h3>
                <p className="text-slate-300 text-sm">
                  Check your story against four qualities drawn from storytelling research (specific detail, sensory immersion, memorable hooks and shared experience) with a quick text checklist. It counts features in your writing; it can't measure a listener's brain.
                </p>
              </Card>

              {/* Additional tools indicator */}
              <Card className="p-6 bg-gradient-to-br from-slate-700/30 to-slate-800/30 border-slate-600/30 flex items-center justify-center">
                <div className="text-center">
                  <Sparkles className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-white font-bold text-lg mb-1">Plus 3 More Tools</p>
                  <p className="text-slate-400 text-sm">Story Rehearsal, Listening Practice, Family Story Scale</p>
                </div>
              </Card>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Button
                size="lg"
                onClick={() => navigate('/toolkit')}
                className="group inline-flex items-center whitespace-nowrap px-6 sm:px-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                <Target className="w-5 h-5 mr-2 flex-shrink-0" />
                Explore All 9 Tools
                <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="mt-4 text-slate-400">
                Most tools let you download your work • Use them as many times as you need • Build your stories with confidence
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-800/30 relative overflow-hidden">
        {/* Subtle background patterns */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64">
            <img src="/images/mod-05-arc.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64">
            <img src="/images/mod-04-structure.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Rotating Content Panel */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-4xl mx-auto"
          >
            <motion.div
              key={currentBenefitSlide}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="min-h-[280px] flex flex-col justify-center"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {benefitSlides[currentBenefitSlide].title}
              </h2>
              <p className="text-xl text-purple-300 mb-4 italic">
                {benefitSlides[currentBenefitSlide].subtitle}
              </p>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
                {benefitSlides[currentBenefitSlide].description}
              </p>
            </motion.div>

            {/* Slide indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {benefitSlides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBenefitSlide(idx)}
                  className={`transition-all duration-300 ${
                    idx === currentBenefitSlide
                      ? 'w-12 h-2 bg-purple-500'
                      : 'w-2 h-2 bg-slate-600 hover:bg-slate-500'
                  } rounded-full`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Deeper Connections",
                description: "Move beyond surface-level exchanges. Build genuine relationships through stories that reveal who you really are and create space for others to do the same.",
                color: "from-pink-500 to-rose-500"
              },
              {
                icon: TrendingUp,
                title: "Career Impact",
                description: "Nail interviews with compelling origin stories, lead with narrative clarity, and build the personal brand that opens doors through authentic storytelling.",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: Users,
                title: "Social Confidence",
                description: "Walk into any room, from networking events to dinner parties, with stories that engage, entertain, and create genuine connection beyond small talk.",
                color: "from-purple-500 to-indigo-500"
              },
              {
                icon: Shield,
                title: "Make Sense of Struggle",
                description: "Transform struggle stories into narratives that honour the difficulty whilst highlighting your growth, a shift that one study of people in therapy found came before feeling better (Adler, 2012).",
                color: "from-amber-500 to-orange-500"
              },
              {
                icon: Lightbulb,
                title: "Self-Understanding",
                description: "The stories you tell reveal patterns you can't see. Map your narrative landscape and discover which stories serve you and which ones don't.",
                color: "from-green-500 to-emerald-500"
              },
              {
                icon: Sparkles,
                title: "Authentic Expression",
                description: "Learn to share vulnerability appropriately: not too much (oversharing), not too little (shutting down). Calibrate connection with our 10% rule of thumb.",
                color: "from-yellow-500 to-amber-500"
              }
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-6 h-full hover:scale-105 transition-transform">
                    <div className={`w-14 h-14 bg-gradient-to-br ${benefit.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                    <p className="text-slate-300 leading-relaxed">{benefit.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Research Insight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <Card className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <Brain className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <div className="text-center">
                <p className="text-2xl text-white font-medium mb-4">
                  Dan McAdams&apos; narrative identity research describes identity as a story we keep telling and revising.
                </p>
                <footer className="text-slate-400">Based on narrative identity research at Northwestern University</footer>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* What You Get - Real Stats */}
      <section className="py-12 bg-slate-800/50 backdrop-blur-xl">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {realStats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <Icon className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-white">{stat.number}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey of Mastery Section */}
      <section className="py-20 bg-slate-800/30 relative overflow-hidden">
        {/* Decorative gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your Journey From Scattered to
              <span className="gradient-text"> Master Storyteller</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Every craft has levels of mastery. These are the skills you'll develop as you progress
              through the storytelling modules.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full relative overflow-hidden">
                {/* Small decorative art */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none">
                  <img src="/images/mod-01-inventory.png" alt="" className="w-full h-full object-cover" />
                </div>

                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mb-4 mx-auto relative z-10">
                  <span className="text-white font-bold">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 text-center">Foundation</h3>
                <p className="text-purple-300 text-center text-sm mb-4">Building your narrative awareness</p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Map your story landscape</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Understand what makes stories stick</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Transform descending narratives</span>
                  </li>
                </ul>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full relative overflow-hidden">
                {/* Small decorative art */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none">
                  <img src="/images/mod-04-structure.png" alt="" className="w-full h-full object-cover" />
                </div>

                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4 mx-auto relative z-10">
                  <span className="text-white font-bold">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 text-center">Essential</h3>
                <p className="text-blue-300 text-center text-sm mb-4">Developing your craft</p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Master the 5-part structure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Craft compelling emotional arcs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Calibrate vulnerability appropriately</span>
                  </li>
                </ul>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full relative overflow-hidden">
                {/* Small decorative art */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none">
                  <img src="/images/mod-07-delivery.png" alt="" className="w-full h-full object-cover" />
                </div>

                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4 mx-auto relative z-10">
                  <span className="text-white font-bold">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 text-center">Advanced</h3>
                <p className="text-green-300 text-center text-sm mb-4">Mastering delivery and receiving</p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Perfect presence and pacing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Listen empathically to others' stories</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Create storytelling magic</span>
                  </li>
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why This Approach Works */}
      <section className="overflow-x-clip py-20 relative">
        {/* Decorative background art */}
        <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none">
          <img src="/images/mod-02-impact.png" alt="" className="absolute -top-20 -left-20 w-96 h-96 object-cover rotate-45" />
          <img src="/images/mod-06-vulnerability.png" alt="" className="absolute -bottom-20 -right-20 w-96 h-96 object-cover -rotate-45" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why This
              <span className="gradient-text"> Actually Works</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Because we're not teaching tricks. We're teaching you to understand the stories you're already telling yourself, and how to reshape them.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <Sparkles className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Grounded in Research and Craft
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Techniques drawn from narrative psychology and memory research, plus craft advice from working storytellers. Want to know why some stories stick in memory whilst others vanish? Module 2 covers it. We&apos;ll show you how stories work, and how to use that knowledge.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <Heart className="w-10 h-10 text-pink-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Authentic, Not Performative
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  We're teaching you principles, not scripts. How to be genuinely specific. Emotionally honest. Appropriately vulnerable. This isn't about becoming a slick performer; it's about sharing your actual self in ways that connect.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <Brain className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Personalised to You
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Everyone tells stories differently. Our assessment suggests your
                  narrative strengths and growth edges, so you can focus on what matters most for you.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <Coffee className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Actually Interesting
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Look, learning doesn't have to be tedious. We're going for "great radio storytelling meets fascinating research" not "corporate training video you have to click through." If you're curious about how stories work in your mind, you'll dig this.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Questions? We've Got Answers
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: "I'm not a natural storyteller. Can this really help?",
                a: "That's exactly who this is for! 'Natural' storytellers just practised accidentally. We make the practice deliberate and systematic."
              },
              {
                q: "How is this different from other storytelling courses?",
                a: "No scripts, and no templates to memorise. Just principles drawn from narrative psychology, memory research and storytelling craft, applied to your actual stories. We teach you how storytelling works, not what to say."
              },
              {
                q: "How much time does it really take?",
                a: "Each of the 8 modules takes about 40 minutes (38 to 42), plus exercises if you do them. The toolkit tools take 10-20 minutes each, as needed. Go at whatever pace suits you."
              },
              {
                q: "What if I'm already good at telling stories?",
                a: "Great! The assessment shows your strengths and suggests where to focus next. Even skilled storytellers discover blind spots."
              },
              {
                q: "Is this just for professional speakers or writers?",
                a: "Nope! This is for anyone who tells stories: in job interviews, on dates, at family gatherings, or over coffee. The skills transfer everywhere."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-purple-400 mt-0.5" />
                    {faq.q}
                  </h3>
                  <p className="text-slate-300 ml-8">{faq.a}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <img src="/images/mod-03-oscillating.png" alt="" className="absolute top-0 left-0 w-full h-64 object-cover" />
          <img src="/images/mod-08-story-listening.png" alt="" className="absolute bottom-0 right-0 w-full h-64 object-cover" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Card className="p-12 bg-gradient-to-br from-purple-600/20 to-pink-600/20 text-center backdrop-blur-sm">
              <Gift className="w-16 h-16 text-yellow-400 mx-auto mb-6" />

              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Change Your Story?
              </h2>

              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Start with an 8-10 minute assessment that shows how you naturally tell stories. Then dig into the skills that'll transform not just how you share stories, but how you understand yourself. No pressure. Just you and the narratives you've been telling.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  size="lg"
                  onClick={() => navigate(user ? '/assessment' : '/signup')}
                  className="group"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Your Assessment
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              <p className="text-sm text-slate-400">
                8-10 minutes to complete • Instant personalised results • 8 modules + 9 tools to explore
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Cross-Promotion: Story Threads & Story Crew */}
      <section className="overflow-x-clip py-20 bg-gradient-to-br from-slate-900/90 via-purple-900/20 to-slate-900/90 border-y border-slate-800/50">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="inline-block mb-4"
            >
              <span className="text-6xl">🎯</span>
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Put Your Storytelling Mastery to Work
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              You're learning the craft. Now discover where these skills take you, from strengthening family
              bonds to bringing joy to any gathering.
            </p>
          </motion.div>

          {/* App Cards Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">

            {/* Story Threads Card */}
            <motion.a
              href="https://storythreads.divedeepdialogues.com"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="group"
            >
              <Card className="h-full p-8 bg-gradient-to-br from-orange-900/30 to-red-900/30 border-2 border-orange-500/30 hover:border-orange-400/60 hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div className="text-5xl">🏕️</div>
                  <div className="px-3 py-1 bg-orange-500/20 border border-orange-400/40 rounded-full text-orange-300 text-xs font-semibold">
                    DEEPEN CONNECTIONS
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-orange-100 mb-3 group-hover:text-orange-50 transition-colors">
                  Story Threads
                </h3>

                <p className="text-slate-300 mb-6 leading-relaxed">
                  Ready to apply your mastery where it matters most? Bring your <span className="text-orange-300 font-semibold">family storytelling</span> into
                  the open, struggles as well as strengths. Techniques designed for connection across generations.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    </div>
                    <span className="text-sm text-slate-300">Use oscillating narratives to tell family stories that hold struggle and strength</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    </div>
                    <span className="text-sm text-slate-300">A private, careful approach to family narratives</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    </div>
                    <span className="text-sm text-slate-300">Tools to craft stories that strengthen generational bonds</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-orange-300 font-semibold group-hover:gap-3 transition-all">
                  <span>Transform Family Bonds</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </Card>
            </motion.a>

            {/* Story Crew Card (took the place of Just For Fun, retired October 2026) */}
            <motion.a
              href="https://storycrew.divedeepdialogues.com"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="group"
            >
              <Card className="h-full p-8 bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-2 border-purple-500/30 hover:border-purple-400/60 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div className="text-5xl">🎬</div>
                  <div className="px-3 py-1 bg-purple-500/20 border border-purple-400/40 rounded-full text-purple-300 text-xs font-semibold">
                    PRACTISE WITH JOY
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-purple-100 mb-3 group-hover:text-purple-50 transition-colors">
                  Story Crew
                </h3>

                <p className="text-slate-300 mb-6 leading-relaxed">
                  The best way to master storytelling? <span className="text-purple-300 font-semibold">Practise in a playful, low-pressure setting</span>.
                  A story game for family and friends: one person asks, one person tells, and a phone catches it. Free, with no account.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                    </div>
                    <span className="text-sm text-slate-300">Quick Story, Quests and a Story Night for the whole room</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                    </div>
                    <span className="text-sm text-slate-300">Watch it back, star the best moment, and keep the stories you're glad you told</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                    </div>
                    <span className="text-sm text-slate-300">At the dinner table, on a long drive, or at a party where everyone is in on it</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-purple-300 font-semibold group-hover:gap-3 transition-all">
                  <span>Practise Your Craft</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </Card>
            </motion.a>
          </div>

          {/* Bottom Tagline */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="text-slate-400 italic">
              Master the craft. <span className="text-purple-400 font-semibold">Apply it to transform lives.</span> Practise it with joy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-slate-400 mb-4">
              Built with ❤️ by Dive Deep Dialogues
            </p>
            <p className="text-sm text-slate-400">
              © 2025 SILVER WOLF Productions. Because the stories you tell shape who you become.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
